import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { getApiData } from "../../utils/APIHelper";
import { Setting } from "../../utils/Setting";
import { toast } from "react-toastify";
import CInput from "../CInput";

const BillingInformationModal = ({ open, onClose, onSave, initialData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [billingData, setBillingData] = useState({
    beneficiaryName: "",
    iban: "",
    bankName: "",
    bankAccount: "",
    swiftCode: "",
    address: "",
  });
  const [buttonLoader, setButtonLoader] = useState(false);
  const [errors, setErrors] = useState({
    iban: "",
    bankAccount: "",
    swiftCode: "",
  });

  // Validation functions
  const validateIBAN = (iban) => {
    if (!iban) return "";
    
    // Remove spaces and convert to uppercase
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    
    // Basic IBAN format check: 2 letters + 2 digits + up to 30 alphanumeric characters
    const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,30}$/;
    
    if (!ibanRegex.test(cleanIban)) {
      return "Invalid IBAN format. Must be 2 letters + 2 digits + 4-30 alphanumeric characters";
    }
    
    // IBAN length should be between 15-34 characters
    if (cleanIban.length < 15 || cleanIban.length > 34) {
      return "IBAN must be between 15-34 characters";
    }
    
    return "";
  };

  const validateBankAccount = (account) => {
    if (!account) return "";
    
    // Remove spaces and special characters except hyphens
    const cleanAccount = account.replace(/[^\w-]/g, '');
    
    // Bank account should be 4-34 characters and contain only alphanumeric characters and hyphens
    const accountRegex = /^[A-Z0-9-]{4,34}$/i;
    
    if (!accountRegex.test(cleanAccount)) {
      return "Invalid bank account format. Use 4-34 alphanumeric characters and hyphens only";
    }
    
    return "";
  };

  const validateSwiftCode = (swift) => {
    if (!swift) return "";
    
    // Convert to uppercase and remove spaces
    const cleanSwift = swift.replace(/\s/g, '').toUpperCase();
    
    // SWIFT code should be 8 or 11 characters: 4 letters (bank code) + 2 letters (country) + 2 characters (location) + optional 3 characters (branch)
    const swiftRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
    
    if (!swiftRegex.test(cleanSwift)) {
      return "Invalid SWIFT code format. Must be 8 or 11 characters (e.g., ABCDUS33 or ABCDUS33XXX)";
    }
    
    return "";
  };

  useEffect(() => {
    if (open && initialData) {
      setBillingData({
        beneficiaryName: initialData.beneficiaryName || "",
        iban: initialData.iban || "",
        bankName: initialData.bankName || "",
        bankAccount: initialData.bankAccount || "",
        swiftCode: initialData.swiftCode || "",
        address: initialData.address || "",
      });
    }
  }, [open]); // Only depend on 'open', not 'initialData'

  const handleInputChange = (field, value) => {
    setBillingData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Validate specific fields
    let errorMessage = "";
    if (field === "iban") {
      errorMessage = validateIBAN(value);
    } else if (field === "bankAccount") {
      errorMessage = validateBankAccount(value);
    } else if (field === "swiftCode") {
      errorMessage = validateSwiftCode(value);
    }

    setErrors((prev) => ({
      ...prev,
      [field]: errorMessage,
    }));
  };

  // Check if at least one field has a valid value
  const hasValidField = () => {
    const ibanError = validateIBAN(billingData.iban);
    const bankAccountError = validateBankAccount(billingData.bankAccount);
    const swiftCodeError = validateSwiftCode(billingData.swiftCode);

    // Check if at least one field has a value
    const hasBeneficiaryName = billingData.beneficiaryName && billingData.beneficiaryName.trim() !== "";
    const hasValidIban = billingData.iban && !ibanError;
    const hasBankName = billingData.bankName && billingData.bankName.trim() !== "";
    const hasValidBankAccount = billingData.bankAccount && !bankAccountError;
    const hasValidSwiftCode = billingData.swiftCode && !swiftCodeError;
    const hasAddress = billingData.address && billingData.address.trim() !== "";

    return hasBeneficiaryName || hasValidIban || hasBankName || hasValidBankAccount || hasValidSwiftCode || hasAddress;
  };

  const handleSave = async () => {
    try {
      // Validate all fields before saving
      const ibanError = validateIBAN(billingData.iban);
      const bankAccountError = validateBankAccount(billingData.bankAccount);
      const swiftCodeError = validateSwiftCode(billingData.swiftCode);

      setErrors({
        iban: ibanError,
        bankAccount: bankAccountError,
        swiftCode: swiftCodeError,
      });

      // Check if there are any validation errors
      if (ibanError || bankAccountError || swiftCodeError) {
        return;
      }

      setButtonLoader(true);
      
      const data = {
        beneficiary_name: billingData.beneficiaryName,
        iban: billingData.iban,
        bank_name: billingData.bankName,
        bank_account: billingData.bankAccount,
        swift_code: billingData.swiftCode,
        address: billingData.address,
      };

      const response = await getApiData(
        Setting.endpoints.addBillingInfo,
        "POST",
        data
      );

      if (response.success) {
        toast.success(response.message || 'Billing information saved successfully!');
        onSave(billingData);
        onClose();
      } else {
        toast.error(response.message || 'Failed to save billing information');
      }
    } catch (error) {
      console.log("🚀 ~ BillingInformationModal ~ handleSave ~ error:", error);
      toast.error(error.toString() || 'Something went wrong. Please try again.');
    } finally {
      setButtonLoader(false);
    }
  };

  const handleClose = () => {
    setBillingData({
      beneficiaryName: "",
      iban: "",
      bankName: "",
      bankAccount: "",
      swiftCode: "",
      address: "",
    });
    setErrors({
      iban: "",
      bankAccount: "",
      swiftCode: "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: { xs: 0, md: 2 } },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: { xs: "17px", md: "20px" }, letterSpacing: 0 }}>
          Add your billing information
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: { xs: 2.5, md: 4 } }}>
        {/* Beneficiary Name */}
        <CInput
          label="Beneficiary Name"
          value={billingData.beneficiaryName}
          onChange={(e) => handleInputChange("beneficiaryName", e.target.value)}
          placeholder="Beneficiary Name"
        />

        {/* IBAN */}
        <CInput
          label="IBAN"
          value={billingData.iban}
          onChange={(e) => handleInputChange("iban", e.target.value)}
          placeholder="Enter IBAN (e.g., GB82WEST12345698765432)"
          error={!!errors.iban}
          helperText={errors.iban}
        />

        {/* Bank Name */}
        <CInput
          label="Bank Name"
          value={billingData.bankName}
          onChange={(e) => handleInputChange("bankName", e.target.value)}
          placeholder="Enter bank name"
        />

        {/* Bank Account */}
        <CInput
          label="Bank Account"
          value={billingData.bankAccount}
          onChange={(e) => handleInputChange("bankAccount", e.target.value)}
          placeholder="Enter bank account number (4-34 alphanumeric characters)"
          error={!!errors.bankAccount}
          helperText={errors.bankAccount}
        />

        {/* SWIFT Code */}
        <CInput
          label="SWIFT code"
          value={billingData.swiftCode}
          onChange={(e) => handleInputChange("swiftCode", e.target.value)}
          placeholder="Enter SWIFT code (e.g., ABCDUS33 or ABCDUS33XXX)"
          error={!!errors.swiftCode}
          helperText={errors.swiftCode}
        />

        {/* Address */}
        <CInput
          label="Address"
          value={billingData.address}
          onChange={(e) => handleInputChange("address", e.target.value)}
          placeholder="Enter your address"
          multiline
          rows={4}
        />
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, md: 3 }, pt: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={buttonLoader || !hasValidField()}
          sx={{
            textTransform: "none",
            backgroundColor: "#4D6BF6",
            "&:hover": {
              backgroundColor: "#3B5BDB",
            },
            "&:disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {buttonLoader ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "white" }} />
              Saving...
            </Box>
          ) : (
            "Add bank information"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingInformationModal;
